import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CircularProgress, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import Seo from '../components/Seo';

const STATES = ('AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH '
  + 'NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY').split(' ');

const EMPTY = { street: '', unit: '', city: '', state: 'FL', zip: '' };

/**
 * Public page (link emailed to technicians with no address on file): enter
 * your home address. The signed token in the URL is the only authorization.
 */
const TechnicianAddressPage = () => {
  const { token } = useParams();
  const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    fetch(`${apiBase}/api/public/technician-address/${token}`)
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'This link is invalid or has expired.');
        setInfo(data);
      })
      .catch((err) => setLoadError(err.message));
  }, [apiBase, token]);

  const change = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d+[A-Za-z]?\s+\S/.test(form.street.trim())) return setError('Please enter the street number and street name (e.g. 123 Main St).');
    if (form.city.trim().length < 2) return setError('Please enter your city.');
    if (!/^\d{5}(-\d{4})?$/.test(form.zip.trim())) return setError('Please enter a 5-digit ZIP code.');
    setSaving(true);
    try {
      const res = await fetch(`${apiBase}/api/public/technician-address/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not save your address.');
      setSaved(data.residentialAddress);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  let content;
  if (loadError) {
    content = <Alert severity="error">{loadError}</Alert>;
  } else if (!info) {
    content = <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>;
  } else if (saved) {
    content = (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <CheckCircle color="success" sx={{ fontSize: 56 }} />
        <Typography variant="h6" sx={{ mt: 1 }}>Thank you{info.firstName ? `, ${info.firstName}` : ''}!</Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>We saved your address:</Typography>
        <Typography sx={{ fontWeight: 'bold', mt: 0.5 }}>{saved}</Typography>
        <Typography color="text.secondary" sx={{ mt: 2 }}>You can close this page.</Typography>
      </Box>
    );
  } else {
    content = (
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Typography variant="h6" gutterBottom>Hi{info.firstName ? ` ${info.firstName}` : ''}, what's your home address?</Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          We use it to offer you jobs close to where you live. It's never shared with customers.
        </Typography>
        {info.currentAddress && (
          <Alert severity="info" sx={{ mb: 2 }}>Address on file: {info.currentAddress}</Alert>
        )}
        <Stack spacing={2}>
          <TextField label="Street address" placeholder="123 Main St" value={form.street} onChange={change('street')} required fullWidth autoComplete="address-line1" />
          <TextField label="Apt / unit (optional)" value={form.unit} onChange={change('unit')} fullWidth autoComplete="address-line2" />
          <TextField label="City" value={form.city} onChange={change('city')} required fullWidth autoComplete="address-level2" />
          <Stack direction="row" spacing={2}>
            <TextField select label="State" value={form.state} onChange={change('state')} sx={{ width: 110 }} autoComplete="address-level1">
              {STATES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField
              label="ZIP code"
              value={form.zip}
              onChange={change('zip')}
              required
              fullWidth
              autoComplete="postal-code"
              inputProps={{ inputMode: 'numeric', maxLength: 10 }}
            />
          </Stack>
          {error && <Alert severity="error">{error}</Alert>}
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {saving ? 'Saving…' : 'Save my address'}
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '80vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', px: 2, py: { xs: 3, sm: 6 } }}>
      <Seo title="Your home address" noindex />
      <Card sx={{ width: '100%', maxWidth: 480, p: { xs: 2.5, sm: 4 } }}>
        {info?.companyName && (
          <Typography variant="overline" color="primary" sx={{ fontWeight: 'bold' }}>{info.companyName}</Typography>
        )}
        {content}
        {info && !saved && (info.contactEmail || info.contactPhone) && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Questions? Contact us at {[info.contactEmail, info.contactPhone].filter(Boolean).join(' or ')}.
          </Typography>
        )}
      </Card>
    </Box>
  );
};

export default TechnicianAddressPage;
